import { ChangeDetectionStrategy, Component, input, inject } from '@angular/core';
import { Bucket } from '../../types';
import { GroupService } from '../../services/group.service';
import { DrawerService } from '../../services/drawer.service';
import {
  moveItemInArray,
  CdkDragDrop,
  transferArrayItem,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
} from '@angular/cdk/drag-drop';
import { CategoryBucketSummaryComponent } from '../category-bucket-summary/category-bucket-summary.component';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { BudgetBulkEditPendingBucket, BudgetBulkEditService } from '../../services/budget-bulk-edit.service';

@Component({
  selector: 'app-category-bucket-list',
  templateUrl: './category-bucket-list.component.html',
  styleUrl: './category-bucket-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDropList, CdkDrag, CategoryBucketSummaryComponent, CdkDragHandle, UiIconComponent],
})
export class CategoryBucketListComponent {
  private groupService = inject(GroupService);
  private drawer = inject(DrawerService);
  bulkEdit = inject(BudgetBulkEditService);

  readonly groupId = input('');
  readonly buckets = input<Bucket[]>([]);
  readonly archiveCandidates = input<Bucket[]>([]);
  readonly reorder = input(false);
  readonly currentMonthYear = input('');
  readonly connectedListIds = input<string[]>([]);

  bucketTrackBy(_index: number, bucket: Bucket): string {
    return bucket.id;
  }

  bucketSortUpdate = (event: CdkDragDrop<Bucket[]>): void => {
    const bucket = event.item.data;
    const previousIndex = event.previousContainer.data.findIndex(item => item.id === bucket.id);
    const currentIndex = this.bucketDropIndex(event.currentIndex);
    if (previousIndex < 0) return;

    if (event.previousContainer === event.container) {
      if (previousIndex === currentIndex) return;
      moveItemInArray(this.buckets(), previousIndex, currentIndex);
    } else transferArrayItem(event.previousContainer.data, this.buckets(), previousIndex, currentIndex);

    this.bulkEdit.reorderBucket(bucket.id, event.container.id, currentIndex);
    this.bulkEdit.syncBucketOrder({
      [event.previousContainer.id]: event.previousContainer.data.map(item => item.id),
      [event.container.id]: this.buckets().map(item => item.id),
    });
  };

  get activePanel(): string | boolean {
    return this.groupService.activePanel;
  }

  isActivePanel(bucketId: string): boolean {
    return this.activePanel === bucketId;
  }

  openDrawer(bucket: Bucket): void {
    this.drawer.openBucketDrawer(bucket.id);
  }

  closeActivePanel(activePanel: string | false): void {
    if (this.activePanel === activePanel) setTimeout(() => (this.groupService.activePanel = false), 300);
  }

  findAncestorByClass(el: HTMLElement | null, cls: string): HTMLElement | null {
    while ((el = el?.parentElement ?? null) && !el.classList.contains(cls)) {}
    return el;
  }

  visibleBuckets(): Bucket[] {
    return [
      ...this.buckets(),
      ...this.bulkEdit.pendingBucketsForGroup(this.groupId()).map(bucket => this.toPendingBucket(bucket)),
    ];
  }

  private bucketDropIndex(renderedDropIndex: number): number {
    return this.visibleBuckets()
      .slice(0, renderedDropIndex)
      .filter(bucket => this.isPersistedReorderableBucket(bucket)).length;
  }

  private isPersistedReorderableBucket(bucket: Bucket): boolean {
    return !bucket._pendingCreate && !bucket.archivedMonth && !this.bulkEdit.isPendingArchive(bucket.id);
  }

  private toPendingBucket(bucket: BudgetBulkEditPendingBucket): Bucket {
    return {
      id: bucket.id,
      groupId: bucket.groupId,
      amount: bucket.amount,
      createdByUserId: '',
      createdMonth: bucket.monthYear,
      fund: { start: bucket.monthYear, initialAmount: 0 },
      monthYear: bucket.monthYear,
      name: bucket.name,
      sortIndex: 0,
      status: 'ACTIVE',
      total: 0,
      transactions: {
        data: [],
        pageNumber: 1,
        pageSize: 25,
        totalRecords: 0,
        params: { pageNumber: 1, pageSize: 25 },
        _objectType: 'transactionPage',
      },
      type: bucket.type,
      isSetup: true,
      _objectType: 'bucket',
      _pendingCreate: true,
    };
  }
}
